/**
 * Theme management module
 * Handles dark/light theme switching and persistence
 */

export const THEME_KEY = 'theme';

/**
 * Applies the selected theme (dark or light) to the document
 * @param {string} theme - Either 'dark' or 'light'
 * @param {Document} doc - The document object (defaults to global document)
 * @param {Storage} storage - The storage object (defaults to localStorage)
 */
export function applyTheme(theme, doc = document, storage = localStorage) {
  const root = doc.documentElement;
  if (theme === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
  const metaTheme = doc.querySelector('meta[name="theme-color"]');
  if (metaTheme) {
    metaTheme.setAttribute('content', theme === 'dark' ? '#0b1410' : '#059669');
  }
  doc
    .querySelectorAll('[data-theme-toggle]')
    ?.forEach((btn) => btn.setAttribute('aria-pressed', String(theme === 'dark')));
  storage.setItem(THEME_KEY, theme);
}

/**
 * Gets the current theme from the document
 * @param {Document} doc - The document object
 * @returns {string} 'dark' or 'light'
 */
export function getCurrentTheme(doc = document) {
  return doc.documentElement.classList.contains('dark') ? 'dark' : 'light';
}

/**
 * Toggles between dark and light theme
 * @param {Document} doc - The document object
 * @param {Storage} storage - The storage object
 * @returns {string} The new theme
 */
export function toggleTheme(doc = document, storage = localStorage) {
  const current = getCurrentTheme(doc);
  const next = current === 'dark' ? 'light' : 'dark';
  applyTheme(next, doc, storage);
  return next;
}

/**
 * Determines the initial theme based on stored preference or system settings
 * @param {Storage} storage - The storage object
 * @param {Window} win - The window object
 * @returns {string} 'dark' or 'light'
 */
export function detectInitialTheme(storage = localStorage, win = window) {
  const storedTheme = storage.getItem(THEME_KEY);
  if (storedTheme) return storedTheme;

  const prefersDark = win.matchMedia && win.matchMedia('(prefers-color-scheme: dark)').matches;
  return prefersDark ? 'dark' : 'light';
}
